import {useEffect} from 'react';

import {useTranslation} from 'react-i18next';

import {config} from 'config/env';
import {useAppDispatch} from 'hooks';
import ReactNative from 'services/react-native';
import {store} from 'store';
import {updateBasketAddressAction} from 'store/actions/addresses-action';
import {
  resetStateAction,
  setAccessTradeClickIdAction,
  setAppModeAction,
  setCarouselMaxItemsCount,
  setCurrentStoreWithLocationLocalesAction,
  setOmniChannelsVoucherRedemptionEnabled
} from 'store/actions/app-action';
import {setAuthTokensAction} from 'store/actions/auth-action';
import {
  getBasketCalculationAction,
  getBasketRecommendationsAction,
  getMetaDataAction
} from 'store/actions/basket-action';
import {CurrentStoreType} from 'store/types/app';
import {EventMessagesType} from 'types/checkout';
import {isJsonString, removeAlreadySelectedItems} from 'utils';
import {isSyncAddressEnabled, isUnifiedBasketEnabled} from 'utils/helper';

import {AppMode} from '../constants';
import {useSelectedItemsLocalStorage} from './useSelectedItemsLocalStorage';

function useBrowserEventListener(deviceOS?: string) {
  const dispatch = useAppDispatch();
  const {i18n} = useTranslation();
  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();

  const handleUpdateCurrentStore = (data: CurrentStoreType) => {
    dispatch(setCurrentStoreWithLocationLocalesAction(data));
    dispatch(getBasketRecommendationsAction());
  };

  const handleUpdateMetaData = () => {
    if (isSyncAddressEnabled()) {
      dispatch(getMetaDataAction({callCalculate: true}));
    }
  };

  const handleBasketContentUpdated = (productIds?: string[]) => {
    const currentStoreData = store.getState().app.currentStore;
    const selectedItems = selectedItemsLocalStorage.get();
    let filteredNewItemIds: string[] = [];
    if (productIds && productIds.length) {
      // to auto select the added item
      filteredNewItemIds = removeAlreadySelectedItems(productIds, selectedItems);
    }
    const newSelectedItemIds = [...selectedItems, ...filteredNewItemIds];
    selectedItemsLocalStorage.set(newSelectedItemIds);
    if (currentStoreData?.zipcode && currentStoreData?.subdistrict && !isSyncAddressEnabled())
      dispatch(getBasketCalculationAction({isBasketPreviewItemsUpdated: true}));
    handleUpdateMetaData();
  };

  const removeProductFromBasket = (productId: string) => {
    const selectedItems = selectedItemsLocalStorage.get();
    if (productId) {
      const filteredIds = selectedItems?.filter((item: string) => item !== productId);
      selectedItemsLocalStorage.set(filteredIds);
      return;
    }
  };

  const handleMetadataUpdate = (
    data: CurrentStoreType & {
      locale: string;
      currentStoreCodes: string;
      expressStoreCodes: string;
      customerId?: string;
      idToken?: string;
      accessTradeClickId?: string;
    }
  ) => {
    if (isSyncAddressEnabled()) return;
    const currentStoreData = store.getState().app.currentStore;
    const currentATClickId = store.getState().app.accessTradeClickId;
    const appMode = store.getState().app.appMode;
    const currentLocale = i18n.language;
    const currentStoreLocationString = `${currentStoreData?.zipcode}_${currentStoreData?.subdistrict}`;
    const locationFromPlatform = `${data?.zipcode}_${data?.subdistrict}`;
    if (data?.locale && currentLocale !== data?.locale) {
      i18n.changeLanguage(data?.locale);
    }
    if (data?.idToken && data?.customerId) {
      // FIX: testing this to fix calculate 401 issue
      // dispatch(setAuthTokensAction({idToken: data.idToken, customerId: data.customerId}));
    }
    if (data?.accessTradeClickId && !currentATClickId) {
      dispatch(setAccessTradeClickIdAction(data?.accessTradeClickId));
    }
    if (data?.zipcode && data?.subdistrict && currentStoreLocationString !== locationFromPlatform) {
      const storeCodes = data?.currentStoreCodes ? data?.currentStoreCodes.split(',') : [];
      const expressStoreCodes =
        data?.expressStoreCodes && isUnifiedBasketEnabled() ? data?.expressStoreCodes.split(',') : [];
      dispatch(
        setCurrentStoreWithLocationLocalesAction({
          zipcode: String(data.zipcode),
          subdistrict: data.subdistrict,
          storeCodes,
          showStorePickup: !!currentStoreData?.showStorePickup,
          isSameDayDelivery: data?.isSameDayDelivery,
          expressStoreCodes
        })
      );
      dispatch(updateBasketAddressAction());
      if (appMode === AppMode.express && !expressStoreCodes?.length && isUnifiedBasketEnabled()) {
        dispatch(setAppModeAction(AppMode.regular));
      }
    }
  };

  const handleSetIdToken = (idToken: string) => {
    dispatch(setAuthTokensAction({idToken}));
  };

  const handleSelectBasketItem = (productId: string) => {
    const selectedItems = selectedItemsLocalStorage.get();

    if (!selectedItems.includes(productId)) {
      const updatedSelectedItems = [...selectedItems, productId];
      selectedItemsLocalStorage.set(updatedSelectedItems);
    }
  };

  const handleMessage = (event: MessageEvent<any>) => {
    const maknetWebUrl = config.MAKNET_WEB_BASE_URL;
    const expectedOrigin = new URL(maknetWebUrl).origin;

    const isReactNativeView = ReactNative.isReactNativeWebView();
    if (event.origin !== expectedOrigin && !isReactNativeView) {
      return;
    }

    const data =
      isReactNativeView && event.data ? (isJsonString(event.data) ? JSON.parse(event.data) : event.data) : event.data;

    switch (data?.type) {
      case EventMessagesType.UPDATE_BASKET_CURRENT_STORE_DATA:
        handleUpdateCurrentStore(data.currentStorePayload);
        break;
      case EventMessagesType.BASKET_CONTENT_UPDATED: {
        let productIds = [];
        if (data?.productIds) {
          productIds = data?.productIds;
        } else if (data?.productId) {
          productIds = [data.productId];
        }
        handleMetadataUpdate(data?.data);
        handleBasketContentUpdated(productIds);
        break;
      }
      case EventMessagesType.REMOVE_BASKET_PRODUCT: {
        removeProductFromBasket(data?.productId);
        break;
      }
      case EventMessagesType.SET_ID_TOKEN:
        handleSetIdToken(data.idToken);
        break;
      case EventMessagesType.CLOSE_BASKET_DRAWER:
        dispatch(resetStateAction());
        break;
      case EventMessagesType.SET_CAROUSEL_MAX_ITEMS_COUNT:
        dispatch(setCarouselMaxItemsCount(data?.carouselMaxItemsCount));
        break;
      case EventMessagesType.OMNI_CHANNELS_VOUCHER_REDEMPTION_ENABLED:
        dispatch(setOmniChannelsVoucherRedemptionEnabled(data?.data?.isOmniChannelsVoucherRedemptionEnabled));
        break;
      case EventMessagesType.SELECT_BASKET_ITEM:
        handleSelectBasketItem(data?.data?.productId);
        break;
      default:
        console.warn('Unknown message type:', data.type);
    }
  };

  useEffect(() => {
    const isAndroid = ReactNative.isReactNativeWebView() && deviceOS === 'android';
    const listener = isAndroid ? document : window;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    listener.addEventListener('message', handleMessage);
    return () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      listener.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useBrowserEventListener;
